import axios from 'axios';
import { getToken, clearSession } from './authService';
import {
  getLiveSales,
  getLiveProducts,
  getLiveCashTill,
  getLiveCustomers,
  getLiveShifts,
  getLiveAiInsights,
  getLiveVarianceReport,
  getLiveBookings,
  getLiveSupplyChainOrders
} from './realtimePosData';

const DEMO_FALLBACK = process.env.REACT_APP_ENABLE_DEMO_FALLBACK === 'true';

const api = axios.create({
    baseURL: '/api',
    timeout: 10000,
});

const fallbackOrThrow = (error, getter) => {
    if (DEMO_FALLBACK) {
        return getter();
    }
    throw error;
};

// Add token to requests
api.interceptors.request.use((config) => {
    const token = getToken();
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// Resilient Live POS Data & Server Fallback Interceptor
api.interceptors.response.use(
    (response) => response,
    (error) => {
        const url = error.config?.url || '';
        if (error.response && error.response.status === 401) {
            // Secondary AI/invoice service hiccups must never invalidate the active POS session
            if (!url.startsWith('/ai') && !url.startsWith('/invoices')) {
                clearSession();
                if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
                    window.location.href = '/login';
                }
            }
            return Promise.reject(error);
        }

        if (!DEMO_FALLBACK) {
            return Promise.reject(error);
        }

        const method = (error.config?.method || 'get').toLowerCase();

        // Check if error is network / backend 8080 offline / 502 Bad Gateway / timeout
        const isOfflineOrServerError = !error.response ||
            error.response.status === 502 ||
            error.response.status === 503 ||
            error.response.status === 504 ||
            error.response.status >= 500 ||
            error.code === 'ERR_NETWORK' ||
            error.message?.includes('Network Error');

        if (isOfflineOrServerError) {
            if (method === 'get') {
                if (url.includes('/sales')) {
                    return Promise.resolve({ data: getLiveSales(), status: 200, statusText: 'OK' });
                }
                if (url.includes('/products')) {
                    return Promise.resolve({ data: getLiveProducts(), status: 200, statusText: 'OK' });
                }
                if (url.includes('/cash/current')) {
                    return Promise.resolve({ data: getLiveCashTill(), status: 200, statusText: 'OK' });
                }
                if (url.includes('/customers')) {
                    return Promise.resolve({ data: getLiveCustomers(), status: 200, statusText: 'OK' });
                }
                if (url.includes('/shifts')) {
                    return Promise.resolve({ data: getLiveShifts(), status: 200, statusText: 'OK' });
                }
                if (url.includes('/ai/insights')) {
                    return Promise.resolve({ data: getLiveAiInsights(), status: 200, statusText: 'OK' });
                }
                if (url.includes('/variance')) {
                    return Promise.resolve({ data: getLiveVarianceReport(), status: 200, statusText: 'OK' });
                }
                if (url.includes('/bookings')) {
                    return Promise.resolve({ data: getLiveBookings(), status: 200, statusText: 'OK' });
                }
                if (url.includes('/supply-chain')) {
                    return Promise.resolve({ data: getLiveSupplyChainOrders(), status: 200, statusText: 'OK' });
                }
                return Promise.resolve({ data: [], status: 200, statusText: 'OK' });
            }

            // Writes never fall back to a synthetic success. Returning a fake
            // 200 here would tell the operator a sale, shift, or campaign was
            // saved when it never reached the database.
            return Promise.reject(error);
        }

        return Promise.reject(error);
    }
);

// Products
export const fetchProducts = async (params) => {
    try {
        const cleanParams = (params && !params.queryKey) ? params : undefined;
        const res = cleanParams ? await api.get('/products', { params: cleanParams }) : await api.get('/products');
        return Array.isArray(res.data) ? res.data : [];
    } catch (e) {
        return fallbackOrThrow(e, getLiveProducts);
    }
};
export const createProduct = async (data) => (await api.post('/products', data)).data;
export const updateProduct = async (id, data) => (await api.put(`/products/${id}`, data)).data;

// Customers
export const fetchCustomers = async () => {
    try {
        const res = await api.get('/customers');
        return Array.isArray(res.data) ? res.data : [];
    } catch (e) {
        return fallbackOrThrow(e, getLiveCustomers);
    }
};

// Sales
export const fetchSales = async (page, size) => {
    try {
        const config = (page !== undefined || size !== undefined)
            ? { params: { page: page ?? 0, size: size ?? 2500 } }
            : undefined;
        const res = await api.get('/sales', config);
        return Array.isArray(res.data) ? res.data : [];
    } catch (e) {
        return fallbackOrThrow(e, getLiveSales);
    }
};

export const verifyManagerPin = async (pin) => {
    const res = await api.post('/auth/manager-override', { pin });
    return res.data;
};

export const createSale = async (data, managerOverrideToken = null) => {
    return managerOverrideToken
        ? (await api.post('/sales', data, { headers: { 'X-Manager-Override': managerOverrideToken } })).data
        : (await api.post('/sales', data)).data;
};

export const refundSale = async (id, managerOverrideToken = null) => {
    return managerOverrideToken
        ? (await api.post(`/sales/${id}/refund`, {}, { headers: { 'X-Manager-Override': managerOverrideToken } })).data
        : (await api.post(`/sales/${id}/refund`)).data;
};

// Shifts
export const fetchShifts = async () => {
    try {
        const res = await api.get('/shifts');
        return Array.isArray(res.data) ? res.data : [];
    } catch (e) {
        return fallbackOrThrow(e, getLiveShifts);
    }
};

// Employees
export const fetchEmployees = async () => {
    try {
        const res = await api.get('/employees');
        return Array.isArray(res.data) ? res.data : [];
    } catch (e) {
        return fallbackOrThrow(e, () => []);
    }
};

// Dashboard
export const fetchDashboardSummary = async () => (await api.get('/dashboard/summary')).data;

// AI Insights
export const fetchAiInsights = async () => {
    try {
        const res = await api.get('/ai/insights');
        return res.data;
    } catch (e) {
        return fallbackOrThrow(e, getLiveAiInsights);
    }
};
export const updateAiInsightStatus = async (id, status) =>
    (await api.put(`/ai/insights/${id}/status`, { status })).data;
export const queryAi = async (query) => (await api.post('/ai/query', { query }, { timeout: 60000 })).data;
export const chatWithAi = async (message, context) => {
    const res = await api.post('/ai/chat', { message, context }, { timeout: 60000 });
    if (res.data && (res.data.reply || res.data.summary)) {
        return res.data;
    }
    throw new Error('Empty response from AI service');
};
export const checkAiHealth = async () => (await api.get('/ai/health')).data;

// Gift Cards
export const issueGiftCard = async (code, amount) => (await api.post('/gift-cards/issue', { code, amount })).data;
export const redeemGiftCard = async (code, amount) => (await api.post('/gift-cards/redeem', { code, amount })).data;

export const generateCustomerCampaign = async (customerId) => {
    const res = await api.get(`/ai/marketing/campaign/${customerId}`, { timeout: 60000 });
    return res.data;
};

// Invoice Upload
export const uploadInvoice = async (file) => {
    try {
        const formData = new FormData();
        formData.append('file', file);
        const res = await api.post('/invoices/upload', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 90000
        });
        if (Array.isArray(res.data) && res.data.length > 0) {
            return res.data;
        }
        throw new Error('No items returned from backend extraction');
    } catch (err) {
        console.error('[Invoice] Server extraction failed:', err.message);
        throw err;
    }
};

export const confirmInvoiceExtraction = async (products) => (await api.post('/invoices/confirm', products, { timeout: 30000 })).data;

// Marketplace
export const fetchMarketplaceWidgets = async () => (await api.get('/marketplace/widgets')).data;
export const installMarketplaceWidget = async (id) => (await api.post(`/marketplace/widgets/${id}/install`)).data;

// Bookings
export const fetchBookings = async () => {
    try {
        const res = await api.get('/bookings');
        return Array.isArray(res.data) ? res.data : [];
    } catch (e) {
        return fallbackOrThrow(e, getLiveBookings);
    }
};
export const createBooking = async (data) => (await api.post('/bookings', data)).data;

// NLP Query for Dynamic Chart
export const executeNlpQuery = async (query) => (await api.post('/ai/query', { query }, { timeout: 60000 })).data;

// BOM Builder
export const fetchFinishedGoods = async () => (await api.get('/bom/finished-goods')).data;
export const fetchRawMaterials = async () => (await api.get('/bom/raw-materials')).data;
export const fetchBomForFinishedGood = async (id) => (await api.get(`/bom/${id}`)).data;
export const addBomItem = async (finishedGoodId, rawMaterialId, quantityRequired, unitOfMeasure) =>
    (await api.post('/bom', null, { params: { finishedGoodId, rawMaterialId, quantityRequired, ...(unitOfMeasure ? { unitOfMeasure } : {}) } })).data;
export const removeBomItem = async (id) => (await api.delete(`/bom/${id}`)).data;
export const fetchAiBomSuggestions = async (finishedGoodId, payload = null) => (await api.post(`/bom/ai-suggest/${finishedGoodId}`, payload || {}, { timeout: 60000 })).data;
export const saveBatchBomItems = async (finishedGoodId, items) => (await api.post(`/bom/batch-save/${finishedGoodId}`, { items }, { timeout: 30000 })).data;

// Variance Dashboard
export const fetchVarianceReport = async () => {
    try {
        const res = await api.get('/variance/report');
        return Array.isArray(res.data) ? res.data : [];
    } catch (e) {
        return fallbackOrThrow(e, getLiveVarianceReport);
    }
};

// Supply Chain
export const fetchSupplyChainOrders = async () => {
    try {
        const res = await api.get('/supply-chain/orders');
        return Array.isArray(res.data) ? res.data : [];
    } catch (e) {
        return fallbackOrThrow(e, getLiveSupplyChainOrders);
    }
};

export const createSupplyChainOrder = async (orderData) => {
    const res = await api.post('/supply-chain/orders', orderData);
    if (!res.data) {
        throw new Error('Supply chain order was not created');
    }
    return res.data;
};

export const generateSupplyChainDrafts = async () => {
    // Draft generation calls the AI provider once per supplier group and can
    // legitimately take minutes; do not apply the 10s default timeout.
    const res = await api.post('/supply-chain/generate-drafts', null, { timeout: 180000 });
    return res.data;
};

export const approveSupplyChainOrder = async (id) => {
    const res = await api.post(`/supply-chain/orders/${id}/approve`);
    return res.data;
};

export const receiveSupplyChainOrder = async (id, data) => {
    const res = await api.post(`/supply-chain/orders/${id}/receive`, data || {});
    return res.data;
};

export const syncStockDharmaOrders = async () => {
    const res = await api.post('/stock-dharma/orders/sync');
    return res.data;
};

export const syncStockDharmaProducts = async (purgeExistingDemoData = false) => {
    const res = await api.post(`/stock-dharma/sync-products?purgeExistingDemoData=${purgeExistingDemoData}`);
    return res.data;
};

export const deleteSupplyChainOrder = async (id) => {
    const res = await api.delete(`/supply-chain/orders/${id}`);
    return res.data;
};

export const fetchKitchenTickets = async () => {
    const res = await api.get('/kds/tickets');
    return Array.isArray(res.data) ? res.data : [];
};

// Clients, locations and tenant-scoped users
export const fetchLocations = async (clientId) => {
    const res = await api.get('/locations', {
        params: clientId != null ? { clientId } : undefined,
    });
    return Array.isArray(res.data) ? res.data : [];
};

export const fetchClients = async () => {
    const res = await api.get('/clients');
    return Array.isArray(res.data) ? res.data : [];
};

export const fetchClient = async (id) => (await api.get(`/clients/${id}`)).data;

export const onboardClient = async (payload) => (await api.post('/clients', payload)).data;

export const addClientLocation = async (clientId, payload) =>
    (await api.post(`/clients/${clientId}/locations`, payload)).data;

export const updateClientLocation = async (clientId, locationId, payload) =>
    (await api.put(`/clients/${clientId}/locations/${locationId}`, payload)).data;

export const deleteClientLocation = async (clientId, locationId) =>
    (await api.delete(`/clients/${clientId}/locations/${locationId}`)).data;

// Manual trigger for Clover external order synchronization through the backend proxy
export const triggerCloverSquareSync = async () => {
    const res = await api.get('/clover/pull-orders', { params: { limit: 25 } });
    return { syncedCount: res.data?.ordersFetched || 0, ...res.data };
};

export const syncCloverCategories = async () => {
    const res = await api.post('/clover/sync-categories');
    return res.data;
};

export const syncHistoricalCloverOrders = async (days = 30, maxOrders = 1500) => {
    const res = await api.post('/clover/sync-history', null, { params: { days, maxOrders } });
    return res.data;
};

export default api;
